<script lang="ts" setup>
import A11yDialog from "a11y-dialog";
import type { Ref } from "vue";
import { computed, nextTick, onMounted, ref, watch } from "vue";

import { type Conversation as ConversationType } from "@/Protocol/types/Conversation";
import Conversation from "./Conversation.vue";
import Dialog from "./Dialog.vue";
import Exchange from "./Exchange.vue";
import { TwiageCase } from "@/Case/models/TwiageCase";
import { BIcon } from "bootstrap-vue";

const props = defineProps<{
  askingQuestion: boolean;
  conversation: ConversationType;
  twiageCase: TwiageCase | null;
}>();

const emit = defineEmits<{
  (eventName: "new-question", event: { question?: string }): Promise<void>;
  (eventName: "new-context"): void;
}>();

const protocolDialogElementRef: Ref<{ getDialog: () => A11yDialog } | null> = ref(null);

let protocolDialog: A11yDialog | undefined;

onMounted(() => {
  protocolDialog = protocolDialogElementRef?.value?.getDialog?.();
});

const hide = () => {
  protocolDialog?.hide();
};

const isInCase = computed(() => props.twiageCase?.id !== undefined);

const show = () => {
  protocolDialog?.show();

  if (isInCase.value && !props.conversation.length) {
    emit("new-question", {});
  }
};

const questionFieldName = "question";

const questionText = ref("");

defineExpose({ hide, show });

const submit = (event: Event | KeyboardEvent) => {
  if (props.askingQuestion) return;

  if (event instanceof KeyboardEvent) {
    const form = (event.target as HTMLInputElement).form;
    if (!form) return;
    const formData = new FormData(form);
    const questionFieldValue = formData.get(questionFieldName);

    if (typeof questionFieldValue === "string") {
      emit("new-question", { [questionFieldName]: questionFieldValue });
    }
    questionText.value = "";
    return form.reset();
  }

  const formData = new FormData(event.target as HTMLFormElement);
  const questionFieldValue = formData.get(questionFieldName);

  if (typeof questionFieldValue === "string") {
    emit("new-question", { [questionFieldName]: questionFieldValue });
  }
  questionText.value = "";
  (event.target as HTMLFormElement).reset();
};

const newContext = () => {
  emit("new-context");
};

const open = ref(false);

const toggleOpen = () => {
  open.value = !open.value;
};

const aiDisclaimer =
  "The Pre-Hospital AI Protocol tool is designed to provide guidance and support, but it does not replace your professional judgment. You are responsible for ensuring that all decisions and actions taken are appropriate and align with best practices and professional standards.";

const placeholderText = computed(() => {
  if (isInCase.value) {
    return "Ask a follow up question for this case...";
  }
  return "Ask a protocol question...";
});

const conversationContainer = ref<HTMLElement | null>(null);

const scrollToBottom = async () => {
  await nextTick();
  if (conversationContainer.value) {
    conversationContainer.value.scrollTop = conversationContainer.value.scrollHeight;
  }
};

watch(
  () => props.conversation,
  async () => {
    await scrollToBottom();
  },
  { deep: true },
);
</script>

<template>
  <Dialog ref="protocolDialogElementRef">
    <template #title>
      <div class="protocol-header">
        <h4 class="mb-0">Protocol AI</h4>
        <button
          v-if="props.conversation.length && !props.askingQuestion"
          class="btn btn-outline-primary btn-sm new-conversation-btn"
          @click="newContext"
        >
          New Conversation
        </button>
      </div>
    </template>

    <template #body>
      <div class="protocol-container">
        <!-- Conversation Section -->
        <div ref="conversationContainer" class="conversation-container">
          <Conversation :conversation="props.conversation">
            <Exchange
              v-for="(exchange, index) in props.conversation"
              :key="index"
              :exchange="exchange"
              :streaming="!!exchange.streaming"
            />
          </Conversation>
        </div>

        <div v-if="props.twiageCase?.complaint" class="case-context">
          <div class="case-header" @click="toggleOpen">
            <div class="case-info">
              <b class="me-2" :style="{ color: props.twiageCase?.complaint?.color }">{{
                props.twiageCase?.complaint?.name
              }}</b>
            </div>
            <button v-if="props.twiageCase?.tags?.length" class="btn btn-link btn-sm p-0" :aria-expanded="open">
              <BIcon v-if="open" icon="chevron-down" />
              <BIcon v-else icon="chevron-up" />
            </button>
          </div>

          <Transition name="slide">
            <div v-if="open" class="tags-container">
              <div class="tag-grid">
                <span
                  v-for="tag in props.twiageCase?.tags"
                  :key="tag.id"
                  class="badge bg-secondary tag-item"
                  :title="tag.name"
                  data-tooltip
                >
                  {{ tag.name }}
                </span>
              </div>
            </div>
          </Transition>
        </div>

        <form class="input-section" @submit.prevent="submit">
          <div class="input-group">
            <input
              id="question"
              v-model="questionText"
              class="form-control"
              name="question"
              :required="!isInCase"
              type="text"
              autofocus
              :placeholder="placeholderText"
              @keydown.enter.prevent="submit"
            />
            <button class="btn btn-primary" type="submit">
              <svg width="16" height="16" fill="#fff" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                <path
                  d="M498.1 5.6c10.1 7 15.4 19.1 13.5 31.2l-64 416c-1.5 9.7-7.4 18.2-16 23s-18.9 5.4-28 1.6L284 427.7l-68.5 74.1c-8.9 9.7-22.9 12.9-35.2 8.1S160 493.2 160 480l0-83.6c0-4 1.5-7.8 4.2-10.8L331.8 202.8c5.8-6.3 5.6-16-.4-22s-15.7-6.4-22-.7L106 360.8 17.7 316.6C7.1 311.3 .3 300.7 0 288.9s5.9-22.8 16.1-28.7l448-256c10.7-6.1 23.9-5.5 34 1.4z"
                />
              </svg>
            </button>
          </div>
          <div class="ai-disclaimer">
            <small>{{ aiDisclaimer }}</small>
          </div>
        </form>
      </div>
    </template>
  </Dialog>
</template>

<style lang="scss" scoped>
.protocol-container {
  display: flex;
  flex-direction: column;
  height: 600px;
  max-height: 80vh;
  overflow: hidden;
}

.protocol-header {
  color: #000;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
}

.conversation-container {
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  margin-bottom: 1rem;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.1);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-track {
    background-color: rgba(0, 0, 0, 0.05);
  }
}

.case-context {
  background: linear-gradient(to right, #f8f9fa, #ffffff);
  border-radius: 8px;
  padding: 1rem;
}

.case-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
}

.case-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.tag-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 0.5rem;
  margin-top: 1rem;
}

.tag-item {
  position: relative;
  cursor: help;
  text-overflow: ellipsis;
  overflow: hidden;
}

.input-section {
  padding: 1rem;
  background: #fff;
  margin-top: auto;
}

.transition-icon {
  transition: transform 0.2s ease;
}

.rotate-180 {
  transform: rotate(180deg);
}

.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.ai-disclaimer {
  margin-top: 1rem;
  font-size: 0.875rem;
  color: #6c757d;
  user-select: none;
}

.ai-disclaimer > small {
  color: #6c757d;
  user-select: none;
  text-align: center;
  float: right;
}

@media screen and (max-width: 700px) {
  .protocol-header {
    margin-right: 2rem;
  }
}
</style>
